import { ReactNode, useEffect, useMemo, useState } from 'react';

import { createContentApiFetcher } from 'utils/nextAPI.utils';
import { FYPBanners, FYPChips, FYPCircles, TopMenu } from 'utils/types';

import { log } from 'utils/functions';

import VirgoContentContext from '../context/VirgoContentContext';

type VirgoContentContextProviderProps = {
  children: ReactNode;
  topMenuData: TopMenu;
};

const VirgoContentProvider = ({
  children,
  topMenuData = { status: 'loading', data: [] },
}: VirgoContentContextProviderProps) => {
  const [topMenu, setTopMenu] = useState<TopMenu>(topMenuData);
  const [chips, setChips] = useState<FYPChips>({ status: 'loading', data: [] });
  const [circles, setCircles] = useState<FYPCircles>({ status: 'loading', data: [] });
  const [banners, setBanners] = useState<FYPBanners>({ status: 'loading', data: [] });

  const fetchFYPContent = async () => {
    const allPromises = [];
    const fetcher = createContentApiFetcher();

    allPromises.push(fetcher.get(`/fyp-chips`));
    allPromises.push(fetcher.get(`/fyp-banners`));
    allPromises.push(fetcher.get(`/fyp-circles`));

    const [chipsResult, bannersResult, circlesResult] = await Promise.allSettled(allPromises);

    if (chipsResult.status === 'fulfilled') {
      setChips({ status: 'success', data: chipsResult?.value?.data || [] });
    } else {
      setChips({ status: 'error', data: [] });
      log.error('Failed to fetch fyp chips:', chipsResult.reason);
    }

    if (bannersResult.status === 'fulfilled') {
      setBanners({ status: 'success', data: bannersResult.value.data || [] });
    } else {
      setBanners({ status: 'error', data: [] });
      log.error('Failed to fetch fyp banners:', bannersResult.reason);
    }

    if (circlesResult.status === 'fulfilled') {
      setCircles({ status: 'success', data: circlesResult.value.data || [] });
    } else {
      setCircles({ status: 'error', data: [] });
      log.error('Failed to fetch fyp circles:', circlesResult.reason);
    }
  };

  useEffect(() => {
    setTopMenu(topMenuData);
  }, []);

  const state = useMemo(
    () => ({
      chips,
      banners,
      circles,
      topMenu: topMenu?.data,
      fetchFYPContent,
    }),
    [banners, chips, circles, topMenu]
  );

  return <VirgoContentContext.Provider value={state}>{children}</VirgoContentContext.Provider>;
};

export default VirgoContentProvider;
